'use client';

import * as React from 'react';
import { Copy, Check } from 'lucide-react';

import { useToast } from '@/components/ui/use-toast';
import { cn } from "@/lib/utils";

interface CopyableResourceBadgeProps {
  /** Display name for the resource (e.g., "Order ID", "Payment ID") */
  name: string;
  /** The ID or value to display */
  value: string;
  /** Additional CSS classes */
  className?: string;
  /** Optional different value to copy (if different from displayed value) */
  copyValue?: string;
  /** Optional variant (kept for backwards compatibility, currently unused) */
  variant?: string;
}

/**
 * CopyableResourceBadge component for displaying resource IDs
 * Clicking copies the ID to clipboard with visual feedback
 */
export function CopyableResourceBadge({
  name,
  value,
  className,
  copyValue,
}: CopyableResourceBadgeProps) {
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  const handleCopy = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const textToCopy = copyValue || value;

      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast({
          title: 'Copied to clipboard',
          description: `${name} copied successfully`,
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast({
          title: 'Failed to copy',
          description: 'Could not copy to clipboard',
          variant: 'destructive',
        });
      }
    },
    [copyValue, value, name, toast],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'group inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 -ml-1.5 transition-colors hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring',
        className,
      )}
      title={`Click to copy ${name}`}
    >
      <span className={cn(
        'font-mono text-sm text-muted-foreground transition-colors',
        copied && 'text-emerald-600 dark:text-emerald-400',
      )}>
        {value}
      </span>
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
      )}
    </button>
  );
}
